// src/utils/mentalCorrelations.ts
// Mental/Cognitive pattern correlation utilities - uses cognitive fields from emotional_symptom_logs

import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';

export interface MentalCorrelation {
  metric: string;
  label: string;
  correlationWithMood: number | null;
  averageValue: number | null;
  sampleSize: number;
}

export interface MentalCorrelationsResult {
  correlations: MentalCorrelation[];
  daysAnalyzed: number;
  dataSufficiency: 'insufficient' | 'minimal' | 'adequate' | 'good';
  summaryInsight: string | null;
}

export interface MentalCorrelationDetail {
  correlation_type: string;
  metric_a: string;
  metric_b: string;
  avg_metric_a_low: number;
  avg_metric_a_high: number;
  avg_metric_b_low: number;
  avg_metric_b_high: number;
  interpretation: string;
  sample_size: number;
}

// Cognitive fields from emotional_symptom_logs
const COGNITIVE_FIELDS = [
  { key: 'rumination_minutes', label: 'Rumination (minutes)', isScore: false },
  { key: 'intrusive_thoughts', label: 'Intrusive Thoughts (count)', isScore: false },
  { key: 'intrusive_thoughts_score', label: 'Intrusive Thoughts Severity', isScore: true },
  { key: 'thought_looping_score', label: 'Thought Looping', isScore: true },
  { key: 'brain_fog_score', label: 'Brain Fog', isScore: true },
  { key: 'concentration_difficulty_score', label: 'Concentration Difficulty', isScore: true },
  { key: 'memory_lapses_score', label: 'Memory Lapses', isScore: true },
  { key: 'word_finding_difficulty_score', label: 'Word-Finding Difficulty', isScore: true },
  { key: 'mental_fatigue_score', label: 'Mental Fatigue', isScore: true },
];

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function calculatePearsonCorrelation(x: number[], y: number[]): number | null {
  if (x.length !== y.length || x.length < 5) return null;
  
  const n = x.length;
  const meanX = average(x);
  const meanY = average(y);
  
  let numerator = 0;
  let sumSqX = 0;
  let sumSqY = 0;
  
  for (let i = 0; i < n; i++) {
    const dx = x[i] - meanX;
    const dy = y[i] - meanY;
    numerator += dx * dy;
    sumSqX += dx * dx;
    sumSqY += dy * dy;
  }
  
  const denominator = Math.sqrt(sumSqX * sumSqY);
  if (denominator === 0) return null;
  
  return numerator / denominator;
}

function getDataSufficiency(sampleSize: number): 'insufficient' | 'minimal' | 'adequate' | 'good' {
  if (sampleSize < 7) return 'insufficient';
  if (sampleSize < 14) return 'minimal';
  if (sampleSize < 30) return 'adequate';
  return 'good';
}

/**
 * Get all mental/cognitive correlations with mood
 */
export async function getMentalCorrelations(
  userId: string,
  days: number = 30
): Promise<MentalCorrelationsResult> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  // Fetch emotional symptom logs with all cognitive fields + mood fields
  const { data: logs, error } = await supabase
    .from('emotional_symptom_logs')
    .select('date, anxiety_level, depression_level, rumination_minutes, intrusive_thoughts, intrusive_thoughts_score, thought_looping_score, brain_fog_score, concentration_difficulty_score, memory_lapses_score, word_finding_difficulty_score, mental_fatigue_score')
    .eq('user_id', userId)
    .gte('date', startDate)
    .order('date', { ascending: true });

  if (error || !logs?.length) {
    return {
      correlations: [],
      daysAnalyzed: 0,
      dataSufficiency: 'insufficient',
      summaryInsight: null
    };
  }

  const correlations: MentalCorrelation[] = [];
  
  // Calculate mood score for each day (average of anxiety + depression)
  const moodScores: number[] = [];
  const validLogs = logs.filter(log => 
    log.anxiety_level !== null || log.depression_level !== null
  );
  
  validLogs.forEach(log => {
    const anxiety = log.anxiety_level ?? 0;
    const depression = log.depression_level ?? 0;
    moodScores.push((anxiety + depression) / 2);
  });

  // Calculate correlation for each cognitive metric
  for (const field of COGNITIVE_FIELDS) {
    const metricValues: number[] = [];
    const correspondingMoodScores: number[] = [];
    
    validLogs.forEach((log, idx) => {
      const value = log[field.key as keyof typeof log];
      if (value !== null && typeof value === 'number') {
        metricValues.push(value);
        correspondingMoodScores.push(moodScores[idx]);
      }
    });
    
    if (metricValues.length >= 5) {
      const correlation = calculatePearsonCorrelation(metricValues, correspondingMoodScores);
      correlations.push({
        metric: field.key,
        label: field.label,
        correlationWithMood: correlation,
        averageValue: average(metricValues),
        sampleSize: metricValues.length
      });
    }
  }

  const maxSampleSize = Math.max(...correlations.map(c => c.sampleSize), 0);
  const dataSufficiency = getDataSufficiency(maxSampleSize);

  // Generate summary insight
  let summaryInsight: string | null = null;
  const significantCorrelations = correlations.filter(c => 
    c.correlationWithMood !== null && Math.abs(c.correlationWithMood) > 0.3
  );
  
  if (significantCorrelations.length > 0) {
    const strongest = significantCorrelations.reduce((a, b) => 
      Math.abs(a.correlationWithMood ?? 0) > Math.abs(b.correlationWithMood ?? 0) ? a : b
    );
    
    if (strongest.correlationWithMood && strongest.correlationWithMood > 0.3) {
      summaryInsight = `Your ${strongest.label.toLowerCase()} tends to increase alongside anxiety and low mood. This is a normal pattern - when the mind is under strain, cognitive symptoms often follow.`;
    } else if (strongest.correlationWithMood && strongest.correlationWithMood < -0.3) {
      summaryInsight = `Interestingly, your ${strongest.label.toLowerCase()} doesn't follow the typical pattern with mood. Your cognitive experience may have different drivers.`;
    }
  } else if (correlations.length > 0) {
    summaryInsight = `Your cognitive patterns don't show strong links to mood yet. This may change as more data accumulates.`;
  }

  return {
    correlations,
    daysAnalyzed: validLogs.length,
    dataSufficiency,
    summaryInsight
  };
}

/**
 * Correlate sleep quality with brain fog
 */
export async function getSleepBrainFogCorrelation(
  userId: string,
  days: number = 60
): Promise<MentalCorrelationDetail | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  const { data: cognitiveLogs } = await supabase
    .from('emotional_symptom_logs')
    .select('date, brain_fog_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  const { data: sleepLogs } = await supabase
    .from('sleep_tracking_logs')
    .select('log_date, quality_rating')
    .eq('user_id', userId)
    .gte('log_date', startDate);
    
  if (!cognitiveLogs?.length || !sleepLogs?.length) return null;
  
  const sleepByDate = new Map(sleepLogs.map(s => [s.log_date, s.quality_rating]));
  const matched: { sleep: number; brainFog: number }[] = [];
  
  cognitiveLogs.forEach(log => {
    const sleepQ = sleepByDate.get(log.date);
    if (log.brain_fog_score !== null && sleepQ !== null) {
      matched.push({ sleep: sleepQ, brainFog: Number(log.brain_fog_score) });
    }
  });
  
  if (matched.length < 7) return null;
  
  const sleepMed = median(matched.map(d => d.sleep));
  const poorSleep = matched.filter(d => d.sleep < sleepMed);
  const goodSleep = matched.filter(d => d.sleep >= sleepMed);
  
  if (poorSleep.length < 3 || goodSleep.length < 3) return null;
  
  const avgFogPoorSleep = average(poorSleep.map(d => d.brainFog));
  const avgFogGoodSleep = average(goodSleep.map(d => d.brainFog));
  const diff = avgFogPoorSleep - avgFogGoodSleep;
  
  let interpretation = '';
  if (diff > 1.5) {
    interpretation = `Poor sleep quality strongly correlates with higher brain fog (avg ${avgFogPoorSleep.toFixed(1)} vs ${avgFogGoodSleep.toFixed(1)} on good sleep nights). Your cognitive clarity is sensitive to rest quality.`;
  } else if (diff > 0.5) {
    interpretation = `There's a gentle link between sleep and brain fog. Better rest may support clearer thinking.`;
  } else {
    interpretation = `Brain fog levels appear relatively stable regardless of sleep quality in your recent data.`;
  }
  
  return {
    correlation_type: 'sleep_brain_fog',
    metric_a: 'Sleep Quality',
    metric_b: 'Brain Fog',
    avg_metric_a_low: average(poorSleep.map(d => d.sleep)),
    avg_metric_a_high: average(goodSleep.map(d => d.sleep)),
    avg_metric_b_low: avgFogPoorSleep,
    avg_metric_b_high: avgFogGoodSleep,
    interpretation,
    sample_size: matched.length
  };
}

/**
 * Correlate meditation with rumination/intrusive thoughts
 */
export async function getMeditationCognitiveCorrelation(
  userId: string,
  days: number = 60
): Promise<MentalCorrelationDetail | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  const { data: cognitiveLogs } = await supabase
    .from('emotional_symptom_logs')
    .select('date, rumination_minutes, intrusive_thoughts_score, thought_looping_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  const { data: activities } = await supabase
    .from('wellness_activities')
    .select('started_at, duration_minutes, activity_type')
    .eq('user_id', userId)
    .ilike('activity_type', '%meditation%')
    .gte('started_at', `${startDate}T00:00:00`);
    
  if (!cognitiveLogs?.length) return null;
  
  const meditationByDate = new Map<string, number>();
  activities?.forEach(a => {
    const date = a.started_at.split('T')[0];
    meditationByDate.set(date, (meditationByDate.get(date) || 0) + (a.duration_minutes || 0));
  });
  
  const matched: { meditation: number; cognitiveLoad: number }[] = [];
  cognitiveLogs.forEach(log => {
    // Create composite cognitive load from available metrics
    const values: number[] = [];
    if (log.rumination_minutes !== null) values.push(Math.min(log.rumination_minutes / 6, 10)); // Normalize to 0-10
    if (log.intrusive_thoughts_score !== null) values.push(Number(log.intrusive_thoughts_score));
    if (log.thought_looping_score !== null) values.push(Number(log.thought_looping_score));
    
    if (values.length > 0) {
      matched.push({
        meditation: meditationByDate.get(log.date) || 0,
        cognitiveLoad: average(values)
      });
    }
  });
  
  if (matched.length < 7) return null;
  
  const withMeditation = matched.filter(d => d.meditation > 0);
  const withoutMeditation = matched.filter(d => d.meditation === 0);
  
  if (withMeditation.length < 3 || withoutMeditation.length < 3) return null;
  
  const avgLoadWith = average(withMeditation.map(d => d.cognitiveLoad));
  const avgLoadWithout = average(withoutMeditation.map(d => d.cognitiveLoad));
  const diff = avgLoadWithout - avgLoadWith;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `Days with meditation show lower cognitive rumination and intrusive thoughts (avg ${avgLoadWith.toFixed(1)} vs ${avgLoadWithout.toFixed(1)}). This practice appears to quieten your mental chatter.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle connection between meditation and reduced mental looping. Consistent practice may deepen this effect.`;
  } else {
    interpretation = `Meditation hasn't shown clear cognitive benefits yet in your data. Different styles or durations may work differently for you.`;
  }
  
  return {
    correlation_type: 'meditation_cognitive',
    metric_a: 'Meditation Practice',
    metric_b: 'Cognitive Load (rumination + intrusions + looping)',
    avg_metric_a_low: 0,
    avg_metric_a_high: average(withMeditation.map(d => d.meditation)),
    avg_metric_b_low: avgLoadWithout,
    avg_metric_b_high: avgLoadWith,
    interpretation,
    sample_size: matched.length
  };
}

/**
 * Correlate movement/exercise with mental fatigue
 */
export async function getMovementMentalFatigueCorrelation(
  userId: string,
  days: number = 60
): Promise<MentalCorrelationDetail | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  const { data: cognitiveLogs } = await supabase
    .from('emotional_symptom_logs')
    .select('date, mental_fatigue_score, concentration_difficulty_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  const { data: checkins } = await supabase
    .from('daily_checkins')
    .select('date, exercise_duration, exercise_intensity')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (!cognitiveLogs?.length || !checkins?.length) return null;
  
  const exerciseByDate = new Map<string, number>();
  checkins.forEach(c => {
    if (c.exercise_duration !== null) {
      const intensity = c.exercise_intensity || 5;
      exerciseByDate.set(c.date, c.exercise_duration * (intensity / 5)); // Weighted by intensity
    }
  });
  
  const matched: { exercise: number; fatigue: number }[] = [];
  cognitiveLogs.forEach(log => {
    const values: number[] = [];
    if (log.mental_fatigue_score !== null) values.push(Number(log.mental_fatigue_score));
    if (log.concentration_difficulty_score !== null) values.push(Number(log.concentration_difficulty_score));
    
    if (values.length > 0 && exerciseByDate.has(log.date)) {
      matched.push({
        exercise: exerciseByDate.get(log.date) || 0,
        fatigue: average(values)
      });
    }
  });
  
  if (matched.length < 7) return null;
  
  const exerciseMed = median(matched.map(d => d.exercise));
  const activedays = matched.filter(d => d.exercise > exerciseMed);
  const sedentaryDays = matched.filter(d => d.exercise <= exerciseMed);
  
  if (activedays.length < 3 || sedentaryDays.length < 3) return null;
  
  const avgFatigueActive = average(activedays.map(d => d.fatigue));
  const avgFatigueSedentary = average(sedentaryDays.map(d => d.fatigue));
  const diff = avgFatigueSedentary - avgFatigueActive;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `Days with more movement show lower mental fatigue (avg ${avgFatigueActive.toFixed(1)} vs ${avgFatigueSedentary.toFixed(1)}). Physical activity appears to support your cognitive energy.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle link between movement and mental clarity. Your brain seems to appreciate when your body moves.`;
  } else {
    interpretation = `Movement doesn't show a strong connection to mental fatigue in your recent data.`;
  }
  
  return {
    correlation_type: 'movement_mental_fatigue',
    metric_a: 'Exercise (weighted)',
    metric_b: 'Mental Fatigue + Concentration',
    avg_metric_a_low: average(sedentaryDays.map(d => d.exercise)),
    avg_metric_a_high: average(activedays.map(d => d.exercise)),
    avg_metric_b_low: avgFatigueSedentary,
    avg_metric_b_high: avgFatigueActive,
    interpretation,
    sample_size: matched.length
  };
}

/**
 * Correlate food quality with cognitive clarity
 */
export async function getFoodCognitiveCorrelation(
  userId: string,
  days: number = 60
): Promise<MentalCorrelationDetail | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
  
  const { data: cognitiveLogs } = await supabase
    .from('emotional_symptom_logs')
    .select('date, brain_fog_score, concentration_difficulty_score, word_finding_difficulty_score')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  const { data: checkins } = await supabase
    .from('daily_checkins')
    .select('date, processed_food_level, healthy_food_level')
    .eq('user_id', userId)
    .gte('date', startDate);
    
  if (!cognitiveLogs?.length || !checkins?.length) return null;
  
  // Calculate food quality score (higher = better)
  const foodQualityByDate = new Map<string, number>();
  checkins.forEach(c => {
    if (c.processed_food_level !== null || c.healthy_food_level !== null) {
      const processed = c.processed_food_level ?? 5;
      const healthy = c.healthy_food_level ?? 5;
      foodQualityByDate.set(c.date, healthy - processed + 5); // Normalize to ~0-10
    }
  });
  
  const matched: { foodQuality: number; cognitiveIssues: number }[] = [];
  cognitiveLogs.forEach(log => {
    const values: number[] = [];
    if (log.brain_fog_score !== null) values.push(Number(log.brain_fog_score));
    if (log.concentration_difficulty_score !== null) values.push(Number(log.concentration_difficulty_score));
    if (log.word_finding_difficulty_score !== null) values.push(Number(log.word_finding_difficulty_score));
    
    if (values.length > 0 && foodQualityByDate.has(log.date)) {
      matched.push({
        foodQuality: foodQualityByDate.get(log.date) || 0,
        cognitiveIssues: average(values)
      });
    }
  });
  
  if (matched.length < 7) return null;
  
  const foodMed = median(matched.map(d => d.foodQuality));
  const goodFood = matched.filter(d => d.foodQuality > foodMed);
  const poorFood = matched.filter(d => d.foodQuality <= foodMed);
  
  if (goodFood.length < 3 || poorFood.length < 3) return null;
  
  const avgIssuesGoodFood = average(goodFood.map(d => d.cognitiveIssues));
  const avgIssuesPoorFood = average(poorFood.map(d => d.cognitiveIssues));
  const diff = avgIssuesPoorFood - avgIssuesGoodFood;
  
  let interpretation = '';
  if (diff > 1) {
    interpretation = `Days with better food quality show fewer cognitive difficulties (avg ${avgIssuesGoodFood.toFixed(1)} vs ${avgIssuesPoorFood.toFixed(1)}). Your brain appears sensitive to what you feed it.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle link between food quality and cognitive clarity. Whole foods may support sharper thinking.`;
  } else {
    interpretation = `Food quality doesn't show a strong link to cognitive symptoms in your recent data.`;
  }
  
  return {
    correlation_type: 'food_cognitive',
    metric_a: 'Food Quality',
    metric_b: 'Cognitive Issues (fog + concentration + word-finding)',
    avg_metric_a_low: average(poorFood.map(d => d.foodQuality)),
    avg_metric_a_high: average(goodFood.map(d => d.foodQuality)),
    avg_metric_b_low: avgIssuesPoorFood,
    avg_metric_b_high: avgIssuesGoodFood,
    interpretation,
    sample_size: matched.length
  };
}

/**
 * Get all mental correlations including detailed breakdowns
 */
export async function getAllMentalCorrelations(
  userId: string,
  days: number = 60
): Promise<MentalCorrelationDetail[]> {
  const results = await Promise.all([
    getSleepBrainFogCorrelation(userId, days).catch(() => null),
    getMeditationCognitiveCorrelation(userId, days).catch(() => null),
    getMovementMentalFatigueCorrelation(userId, days).catch(() => null),
    getFoodCognitiveCorrelation(userId, days).catch(() => null),
  ]);
  
  return results.filter((r): r is MentalCorrelationDetail => r !== null);
}
